import { useCallback } from 'react';

import { SuggestionOption } from '../types';

interface UseKeyboardNavigationProps {
  isOpen: boolean;
  filteredSuggestions: SuggestionOption[];
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  setIsOpen: (isOpen: boolean) => void;
  onSuggestionSelect: (suggestion: SuggestionOption) => void;
  onEscape?: () => void;
  // When true the field is a textarea: Enter/Shift+Enter may insert newlines.
  multiline?: boolean;
}

interface UseKeyboardNavigationReturn {
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  selectHighlighted: () => void;
  close: () => void;
}

export const useKeyboardNavigation = ({
  isOpen,
  filteredSuggestions,
  highlightedIndex,
  setHighlightedIndex,
  setIsOpen,
  onSuggestionSelect,
  onEscape,
  multiline = false,
}: UseKeyboardNavigationProps): UseKeyboardNavigationReturn => {
  const navigateUp = useCallback(() => {
    if (!isOpen || filteredSuggestions.length === 0) return;

    setHighlightedIndex(
      highlightedIndex > 0 ? highlightedIndex - 1 : filteredSuggestions.length - 1
    );
  }, [isOpen, filteredSuggestions.length, highlightedIndex, setHighlightedIndex]);

  const navigateDown = useCallback(() => {
    if (!isOpen || filteredSuggestions.length === 0) return;

    setHighlightedIndex(
      highlightedIndex < filteredSuggestions.length - 1 ? highlightedIndex + 1 : 0
    );
  }, [isOpen, filteredSuggestions.length, highlightedIndex, setHighlightedIndex]);

  const selectHighlighted = useCallback(() => {
    if (!isOpen || highlightedIndex < 0 || highlightedIndex >= filteredSuggestions.length) {
      return;
    }

    onSuggestionSelect(filteredSuggestions[highlightedIndex]);
  }, [isOpen, highlightedIndex, filteredSuggestions, onSuggestionSelect]);

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    onEscape?.();
  }, [setIsOpen, setHighlightedIndex, onEscape]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Multiline: Shift+Enter inserts a newline; close the dropdown.
      if (multiline && e.key === 'Enter' && e.shiftKey) {
        close();
        return;
      }

      if (!isOpen || filteredSuggestions.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          navigateDown();
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateUp();
          break;
        case 'Enter':
          // Multiline: consume Enter only to accept a highlight, else newline.
          if (!multiline || highlightedIndex >= 0) {
            e.preventDefault();
            selectHighlighted();
          }
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
        case 'Tab':
          // Allow tab to close the dropdown and move focus
          close();
          break;
        default:
          // For other keys, don't prevent default to allow normal typing
          break;
      }
    },
    [
      multiline,
      isOpen,
      filteredSuggestions.length,
      highlightedIndex,
      navigateDown,
      navigateUp,
      selectHighlighted,
      close,
    ]
  );

  return {
    handleKeyDown,
    navigateUp,
    navigateDown,
    selectHighlighted,
    close,
  };
};
